import * as Sentry from "@sentry/react";
import { sanitiseUrl, scrubText } from "../analytics/sanitise";

/**
 * Single boundary for error tracking (Sentry), mirroring the PostHog boundary
 * in `../analytics`: only this module talks to `@sentry/react`, so the init
 * config lives in one place and swapping providers later touches this file
 * alone.
 *
 * Unlike PostHog - which allows an opt-in local key against the shared dev
 * project - Sentry must never run on a developer's machine, including a local
 * preview of a production build. A DSN alone cannot be trusted to guarantee
 * that: it can leak into a local `.env` by copy-paste, and a local `.env`
 * survives a rebuild. `VITE_APP_ENV` is the second, independent gate that
 * makes the "never locally" guarantee hold - it is never set in
 * `.env.template` and is only ever set by a CI deploy workflow, so a local
 * checkout cannot satisfy both conditions by accident.
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_ENV = import.meta.env.VITE_APP_ENV;

/** Whether `VITE_APP_ENV` names one of the environments Sentry may report
 *  from. Never true for a local checkout - see the module docs above. */
const isDeployedEnvironment = APP_ENV === "dev" || APP_ENV === "production";

let initialized = false;

/**
 * Apply the analytics URL policy (KAN-253: no URL leaves the browser with a
 * query string or fragment) to an outgoing Sentry event. A concierge link's
 * credential travels in a query string, and a fetch error's message, a request
 * URL or a navigation breadcrumb can all carry one.
 *
 * Reuses the pure helpers behind the PostHog `before_send` hook rather than a
 * second implementation, so the two sinks cannot disagree about what is safe.
 */
export function scrubSentryEvent<T extends Sentry.ErrorEvent>(event: T): T {
  if (event.message) event.message = scrubText(event.message);

  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = scrubText(exception.value);
  }

  if (event.request) {
    if (event.request.url) event.request.url = sanitiseUrl(event.request.url);
    delete event.request.query_string;
    // Cookies are never wanted, and the browser adds the page's Referer to a
    // request context: a concierge link's `?token=` can ride in there.
    delete event.request.cookies;
    const headers = event.request.headers;
    if (headers) {
      for (const [name, value] of Object.entries(headers)) {
        if (name.toLowerCase() === "referer" && typeof value === "string") {
          headers[name] = sanitiseUrl(value);
        }
      }
    }
  }

  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.message) crumb.message = scrubText(crumb.message);
    for (const key of ["url", "from", "to"]) {
      const value = crumb.data?.[key];
      if (crumb.data && typeof value === "string") {
        crumb.data[key] = sanitiseUrl(value);
      }
    }
  }

  return event;
}

/**
 * Initialise Sentry once. No-op unless both `VITE_SENTRY_DSN` is set and
 * `VITE_APP_ENV` names a deployed environment - see the module docs above for
 * why this needs two independent conditions rather than one. Call as early as
 * possible (before the app renders) so bootstrap errors are captured too.
 */
export function initErrorTracking(): void {
  if (initialized) return;
  if (!SENTRY_DSN || !isDeployedEnvironment) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    // Must match the release name any sourcemap upload for this build used
    // (`vite.config.ts`), or Sentry cannot map a reported stack trace back to
    // source.
    release: __GIT_SHA__,
    beforeSend: scrubSentryEvent
  });

  initialized = true;
}

/**
 * Report an error that a boundary or the router already caught. Sentry's own
 * handlers only see uncaught errors and unhandled rejections, and React hands a
 * caught render error to the nearest boundary instead of rethrowing it, so
 * without this call those are invisible to Sentry.
 *
 * Called from `captureCaughtException` in `../analytics`, next to the PostHog
 * `$exception`, so there is one entry point for caught errors. The operator
 * blackout is applied there, not here: `captureCaughtException` simply does
 * not call this helper for a concierge operator's session (PostHog's own
 * suppression covers the PostHog half). No-op until `initErrorTracking` has
 * initialised Sentry (so also in tests, previews and on a developer's
 * machine), and it never throws - it runs inside `componentDidCatch`, where a
 * second error would take down the boundary.
 *
 * Not double-reported: a caught error is never rethrown, so the global handlers
 * (`onerror`, unhandled rejections) do not see it as well - that, not Sentry's
 * Dedupe integration (which only drops an identical event arriving twice in a
 * row), is what keeps it to one report. Sentry's own capture of an uncaught
 * error does not go through `captureCaughtException`, so the blackout does not
 * gate it; only caught errors reported via this path are.
 *
 * `boundary` goes in as a tag (filterable) and the route pathname as extra
 * data; the query string is left out on purpose, as for PostHog.
 */
export function captureCaughtError(
  error: unknown,
  context: { boundary: string; route_path: string }
): void {
  if (!initialized) return;

  try {
    Sentry.captureException(error, {
      tags: { boundary: context.boundary },
      extra: { route_path: context.route_path }
    });
  } catch (reportingError) {
    console.error("[sentry] failed to report exception", reportingError);
  }
}
